/**
 * ResendModal — extracted from Chat.jsx (Feb 2026) for maintainability.
 * Shows pending / wrong LPG entries for a group, lets admin re-dispatch
 * them one-shot. Business logic UNCHANGED — pure code move.
 */
import React, { useState, useEffect } from "react";
import { Clock, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import CopyChip from "@/components/CopyChip";

function ResendModal({ groupId, groupName, onClose }) {
  const [rows, setRows] = useState([]);
  const [unknownAgencies, setUnknownAgencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null);
  const [agencyComment, setAgencyComment] = useState("");

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      // Portal-only source of truth now. Sheet-based row fetching removed
      // per user directive — resend workflow is fully driven by the portal.
      const portalRes = await api
        .get(`/resend/entries?group_id=${groupId}&status=all&days=14`)
        .catch(() => ({ data: { entries: [] } }));
      const portalRows = (portalRes.data.entries || []).map((e) => ({
        row_index: 0,
        entry_id: e.id, // marker — present only on portal entries
        date: (e.created_at || "").slice(0, 10),
        agency: e.agency_resolved,
        lpg_id: e.lpg_id,
        dac: e.dac || "",
        mobile: e.mobile || "",
        status: "via portal",
        confirmed_at: e.confirmed_at ? (e.confirmed_at_display || e.confirmed_at) : "",
        confirmed_by: e.confirmed_by || "",
        received_at: e.received_at_display || "",
        received_by: e.received_by || "",
        attempts: e.attempts || 1,
        max_attempts: e.max_attempts || 5,
        entry_status: e.status,
        agency_ticked: e.agency_ticked,
        staff_ticked: e.staff_ticked,
        agency_comment: e.agency_comment || "",
        staff_comment: e.staff_comment || "",
        created_by: e.created_by,
        created_by_team: e.created_by_team || "",
        reason: e.reason || "",
      }));
      setRows(portalRows);
      setUnknownAgencies([]);
      setSelected(new Set());
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    const id = setInterval(load, 60000);
    // Live sync — the parent Chat WebSocket handler dispatches this event
    // whenever any resend.* type is received. We debounce a tiny bit so a
    // burst of ticks doesn't hammer the endpoint.
    let scheduled = null;
    const onSync = () => {
      if (scheduled) return;
      scheduled = window.setTimeout(() => {
        scheduled = null;
        load();
      }, 250);
    };
    window.addEventListener("resend.sync", onSync);
    return () => {
      clearInterval(id);
      window.removeEventListener("resend.sync", onSync);
      if (scheduled) window.clearTimeout(scheduled);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId]);

  // Portal entries use their unique entry_id; sheet rows use row_index.
  const rowKey = (r) =>
    r.entry_id ? `portal::${r.entry_id}` : `sheet::${r.row_index}::${r.lpg_id}`;

  // Rows agency can still tick — those where agency's tick isn't already set.
  const allKeys = rows.filter((r) => !r.agency_ticked).map(rowKey);
  const allChecked = allKeys.length > 0 && allKeys.every((k) => selected.has(k));
  const toggleAll = () => {
    setSelected(allChecked ? new Set() : new Set(allKeys));
  };
  const toggleOne = (k) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  };

  const confirmTicked = async () => {
    if (!selected.size) return;
    const picked = rows.filter((r) => selected.has(rowKey(r)));
    if (!picked.length) return;
    setSubmitting(true);
    setToast(null);
    try {
      // Portal-only tick — agency's independent tick, optional comment.
      let confirmedCount = 0;
      const errors = [];
      await Promise.all(
        picked
          .filter((r) => r.entry_id)
          .map((r) =>
            api
              .post(`/resend/entries/${r.entry_id}/confirm`, {
                comment: (agencyComment || "").trim() || undefined,
              })
              .then(() => { confirmedCount += 1; })
              .catch((e) => errors.push(formatApiError(e))),
          ),
      );
      if (errors.length && !confirmedCount) {
        setToast({ type: "err", text: errors[0] });
      } else {
        setToast({
          type: "ok",
          text: `${confirmedCount} resend(s) DAC sent · staff ko notify kar diya${errors.length ? ` · ${errors.length} failed` : ""}`,
        });
      }
      setSelected(new Set());
      setAgencyComment("");
      setTimeout(load, 800);
    } catch (e) {
      setToast({ type: "err", text: formatApiError(e) });
    } finally {
      setSubmitting(false);
      setTimeout(() => setToast(null), 4500);
    }
  };

  // Dual-tick logic — a ticket is "done" only when BOTH parties have ticked.
  // A partial state (one tick) still shows in the pending pane so both
  // parties see what's outstanding.
  const isFullyResolved = (r) => r.agency_ticked && r.staff_ticked;
  const pending = rows.filter((r) => !isFullyResolved(r));
  const done = rows.filter((r) => isFullyResolved(r));

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="resend-modal">
      <div className="bg-[#F5F5F0] rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="px-5 py-4 border-b border-[#DCDCD3] flex items-center justify-between">
          <div>
            <div className="text-[10px] font-mono tracking-[0.25em] uppercase text-[#8A6046]">
              Resend DAC · dual-tick confirmation
            </div>
            <h3 className="text-xl font-semibold text-[#1C1C19]" style={{ fontFamily: "Outfit, sans-serif" }}>
              {groupName}
            </h3>
            <p className="text-[11px] text-[#6B6B61] mt-0.5">
              Staff se aayi requests · aap tick karo jab DAC bhej diya · staff apna tick karega jab receive ho · dono tick hone par resolved.
            </p>
          </div>
          <button onClick={onClose} className="text-[#6B6B61] hover:text-[#1C1C19] p-1" data-testid="resend-close">
            ×
          </button>
        </div>

        {unknownAgencies.length > 0 && (
          <div className="mx-5 mt-3 px-3 py-2 bg-amber-50 border border-amber-300 text-amber-900 text-xs rounded" data-testid="resend-unknown-agencies">
            ⚠️ <b>Sheet me {unknownAgencies.length} agency aisi hain jo system me nahi:</b>
            <span className="font-mono ml-1">
              {unknownAgencies.slice(0, 6).map((u) => `${u.agency} (${u.count})`).join(", ")}
              {unknownAgencies.length > 6 ? ` … +${unknownAgencies.length - 6}` : ""}
            </span>
            <div className="text-[10px] mt-1">Admin se kahein: Admin Console → Groups → in agencies ko add karein.</div>
          </div>
        )}
        {err && (
          <div className="mx-5 mt-3 px-3 py-2 bg-red-50 border border-red-300 text-red-800 text-xs rounded">{err}</div>
        )}

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading && <div className="text-center text-sm text-[#6B6B61] py-8">Loading…</div>}
          {!loading && pending.length === 0 && done.length === 0 && (
            <div className="text-center text-sm text-[#6B6B61] py-12">
              Is agency ke liye koi resend request nahi.
            </div>
          )}
          {!loading && pending.length > 0 && (
            <div className="space-y-2 mb-4">
              <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-[#C8553D] mb-2">
                Pending · {pending.length}
              </div>
              {pending.map((r) => {
                const canTick = !r.agency_ticked;
                const inProgress = r.agency_ticked && !r.staff_ticked;
                return (
                  <div
                    key={rowKey(r)}
                    data-testid="resend-row"
                    className="bg-white border border-[#DCDCD3] rounded-lg px-4 py-3 flex items-center gap-3 hover:border-amber-400 transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <CopyChip
                        value={r.lpg_id}
                        label={`Consumer ${r.lpg_id}`}
                        testId="resend-row-copy-lpg"
                        className="text-base font-semibold text-[#1C1C19] hover:text-emerald-700 hover:underline block"
                      />
                      <div className="mt-1 flex items-center gap-2 flex-wrap">
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                            inProgress
                              ? "bg-blue-100 text-blue-800"
                              : "bg-amber-100 text-amber-800"
                          }`}
                        >
                          <Clock className="w-3 h-3" />
                          {inProgress ? "In progress" : "Pending"}
                        </span>
                        {r.entry_id && r.created_by && (
                          <span className="text-[10px] text-[#6B6B61] font-mono">
                            from · {r.created_by}
                            {r.created_by_team ? ` (${r.created_by_team})` : ""}
                          </span>
                        )}
                        {r.entry_id && (r.attempts || 1) > 1 && (
                          <span
                            className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${
                              r.entry_status === "escalated"
                                ? "bg-red-600 text-white"
                                : "bg-[#8A6046] text-white"
                            }`}
                            title={`Attempt ${r.attempts}/${r.max_attempts}`}
                          >
                            {r.entry_status === "escalated" ? "ESCALATED " : "TRY "}
                            {r.attempts}/{r.max_attempts}
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={async () => {
                        if (!canTick) return;
                        setSubmitting(true);
                        try {
                          await api.post(`/resend/entries/${r.entry_id}/confirm`, {});
                          setToast({ type: "ok", text: `DAC marked resent · ${r.lpg_id}` });
                          setTimeout(load, 500);
                        } catch (e) {
                          setToast({ type: "err", text: formatApiError(e) });
                        } finally {
                          setSubmitting(false);
                          setTimeout(() => setToast(null), 3000);
                        }
                      }}
                      disabled={!canTick || submitting}
                      data-testid="resend-row-mark-resent-btn"
                      className={`text-xs px-3 py-1.5 rounded font-semibold border transition-colors whitespace-nowrap ${
                        canTick
                          ? "bg-white text-[#385C4B] border-[#385C4B] hover:bg-[#385C4B] hover:text-white"
                          : "bg-blue-50 text-blue-700 border-blue-200 cursor-default"
                      }`}
                    >
                      {canTick ? "Mark resent" : "Waiting staff"}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
          {!loading && done.length > 0 && (
            <div className="space-y-1">
              <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-emerald-700 mb-2">
                Fully Resolved · {done.length}
              </div>
              {done.slice(0, 50).map((r) => (
                <div
                  key={`done-${rowKey(r)}`}
                  className="px-3 py-2 rounded text-xs font-mono border bg-emerald-100 border-emerald-400"
                >
                  <div className="flex items-center gap-3">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-700" />
                    <span className="text-[#6B6B61]">{r.date}</span>
                    <CopyChip
                      value={r.lpg_id}
                      testId="resend-row-done-copy-lpg"
                      className="text-[#1C1C19] flex-1 font-bold hover:text-emerald-800 hover:underline text-left"
                    />
                    <span
                      className="px-1.5 py-0.5 text-[9px] rounded bg-emerald-700 text-white uppercase tracking-wider font-bold"
                      data-testid="resend-row-received-by-staff"
                    >
                      staff got it · {r.received_by || ""}
                    </span>
                    <span className="text-emerald-800 text-[10px]">{r.received_at || r.confirmed_at}</span>
                  </div>
                  {(r.agency_comment || r.staff_comment) && (
                    <div className="mt-1.5 pl-6 flex flex-col gap-0.5 text-[10px] text-[#6B6B61]">
                      {r.agency_comment && (
                        <span>
                          🅐 <b>Agency</b>: {r.agency_comment}
                        </span>
                      )}
                      {r.staff_comment && (
                        <span>
                          🅢 <b>Staff</b>: {r.staff_comment}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {toast && (
          <div
            className={`px-5 py-3 text-sm font-medium border-t ${
              toast.type === "ok"
                ? "bg-emerald-50 text-emerald-800 border-emerald-300"
                : "bg-red-50 text-red-800 border-red-300"
            }`}
            data-testid="resend-toast"
          >
            {toast.text}
          </div>
        )}
      </div>
    </div>
  );
}

export default ResendModal;
